(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)')

  const reveal = () => {
    const items = document.querySelectorAll('[data-reveal]:not([data-reveal-ready])')
    items.forEach((item, index) => {
      item.dataset.revealReady = 'true'
      item.style.setProperty('--reveal-delay', `${Math.min(index % 4 * 80, 240)}ms`)
    })

    if (reduced.matches || !('IntersectionObserver' in window)) {
      items.forEach(item => item.classList.add('is-visible'))
      return
    }

    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return
      entry.target.classList.add('is-visible')
      observer.unobserve(entry.target)
    }), { threshold: 0.08, rootMargin: '0px 0px -40px' })

    items.forEach(item => observer.observe(item))
  }

  const galleries = () => {
    document.querySelectorAll('[data-case-gallery]:not([data-case-ready])').forEach(gallery => {
      gallery.dataset.caseReady = 'true'
      const track = gallery.querySelector('[data-case-track]')
      const slides = [...gallery.querySelectorAll('[data-case-slide]')]
      const dots = [...gallery.querySelectorAll('[data-case-dot]')]
      const previous = gallery.querySelector('[data-case-previous]')
      const next = gallery.querySelector('[data-case-next]')
      if (!track || slides.length < 2) return

      let active = 0
      let timer
      let inViewport = false
      let isScrolling = false
      let scrollTimer
      const mobile = matchMedia('(max-width: 880px)')

      const paint = () => {
        slides.forEach((slide, index) => {
          const position = (index - active + slides.length) % slides.length
          slide.classList.remove('is-active', 'is-next', 'is-next-two', 'is-previous')
          if (position === 0) slide.classList.add('is-active')
          else if (position === 1) slide.classList.add('is-next')
          slide.setAttribute('aria-hidden', position === 0 ? 'false' : 'true')
        })
        dots.forEach((dot, dotIndex) => {
          if (dotIndex === active) dot.setAttribute('aria-current', 'true')
          else dot.removeAttribute('aria-current')
        })
      }

      const show = index => {
        active = (index + slides.length) % slides.length
        paint()
      }

      const stop = () => {
        clearInterval(timer)
        timer = undefined
      }
      const start = () => {
        stop()
        if (inViewport && !isScrolling && !mobile.matches && !document.hidden && !reduced.matches) {
          timer = setInterval(() => show(active + 1), 5200)
        }
      }

      const onScroll = () => {
        if (!inViewport) return
        isScrolling = true
        stop()
        clearTimeout(scrollTimer)
        scrollTimer = setTimeout(() => {
          isScrolling = false
          start()
        }, 650)
      }

      previous?.addEventListener('click', () => {
        show(active - 1)
        start()
      })
      next?.addEventListener('click', () => {
        show(active + 1)
        start()
      })
      dots.forEach((dot, index) => dot.addEventListener('click', () => {
        show(index)
        start()
      }))

      let pointerStart
      track.addEventListener('pointerdown', event => {
        pointerStart = event.clientX
        stop()
        track.setPointerCapture?.(event.pointerId)
      })
      track.addEventListener('pointerup', event => {
        if (pointerStart === undefined) return
        const distance = pointerStart - event.clientX
        if (Math.abs(distance) > 40) show(distance > 0 ? active + 1 : active - 1)
        pointerStart = undefined
        start()
      })
      track.addEventListener('pointercancel', () => {
        pointerStart = undefined
        start()
      })
      track.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          show(active - 1)
          start()
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault()
          show(active + 1)
          start()
        }
      })

      gallery.addEventListener('pointerenter', stop)
      gallery.addEventListener('pointerleave', start)
      gallery.addEventListener('focusin', stop)
      gallery.addEventListener('focusout', start)
      window.addEventListener('scroll', onScroll, { passive: true })
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop()
        else start()
      })
      paint()
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          inViewport = entries[0].isIntersecting
          if (inViewport) start()
          else {
            clearTimeout(scrollTimer)
            isScrolling = false
            stop()
          }
        }, { threshold: 0.01 })
        observer.observe(gallery)
      } else {
        inViewport = true
        start()
      }
    })
  }

  const accordions = () => {
    document.querySelectorAll('[data-faq-list]:not([data-faq-ready])').forEach(list => {
      list.dataset.faqReady = 'true'
      const items = [...list.querySelectorAll('[data-faq-item]')]
      items.forEach(item => {
        const button = item.querySelector('[data-faq-button]')
        button?.addEventListener('click', () => {
          const willOpen = !item.classList.contains('is-open')
          items.forEach(other => {
            other.classList.remove('is-open')
            other.querySelector('[data-faq-button]')?.setAttribute('aria-expanded', 'false')
          })
          if (willOpen) {
            item.classList.add('is-open')
            button.setAttribute('aria-expanded', 'true')
          }
        })
      })
    })
  }

  const attribution = () => {
    const names = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid']
    const params = new URLSearchParams(window.location.search)
    names.forEach(name => {
      const incoming = params.get(name)
      try {
        if (incoming) sessionStorage.setItem(`nami_${name}`, incoming)
        const value = incoming || sessionStorage.getItem(`nami_${name}`) || ''
        document.querySelectorAll(`[data-attribution="${name}"]`).forEach(input => { input.value = value })
      } catch (_) {
        document.querySelectorAll(`[data-attribution="${name}"]`).forEach(input => { input.value = incoming || '' })
      }
    })
  }

  const leadForms = () => {
    document.querySelectorAll('[data-lead-form]:not([data-lead-form-ready])').forEach(form => {
      form.dataset.leadFormReady = 'true'
      const fields = form.querySelector('[data-form-fields]')
      const success = form.querySelector('[data-form-success]')
      const error = form.querySelector('[data-form-error]')
      const button = form.querySelector('button[type="submit"]')
      const honeypot = form.querySelector('[name="website"]')
      const plan = form.querySelector('[data-plan-select]')
      const budgetField = form.querySelector('[data-budget-field]')
      const budget = budgetField?.querySelector('select')

      const updateBudget = () => {
        if (!plan || !budgetField || !budget) return
        const needsBudget = plan.selectedOptions[0]?.hasAttribute('data-requires-budget') === true
        budgetField.hidden = !needsBudget
        budget.disabled = !needsBudget
        budget.required = needsBudget
        if (!needsBudget) budget.value = ''
      }

      plan?.addEventListener('change', updateBudget)
      updateBudget()

      form.addEventListener('submit', async event => {
        event.preventDefault()
        if (!form.reportValidity()) return

        if (honeypot?.value) {
          fields.hidden = true
          success.hidden = false
          success.focus()
          return
        }

        const source = form.querySelector('[data-source-url]')
        const created = form.querySelector('[data-created-at]')
        if (source) source.value = window.location.href
        if (created) created.value = new Date().toISOString()

        error.hidden = true
        button.disabled = true
        button.textContent = button.dataset.sendingLabel

        try {
          await fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            mode: 'no-cors',
            keepalive: true
          })
          fields.hidden = true
          success.hidden = false
          success.focus()
          form.reset()
        } catch (_) {
          error.hidden = false
          error.focus?.()
        } finally {
          button.disabled = false
          button.textContent = button.dataset.submitLabel
        }
      })
    })
  }

  const counters = () => {
    document.querySelectorAll('[data-performance-proof]:not([data-counter-ready])').forEach(proof => {
      proof.dataset.counterReady = 'true'
      const values = [...proof.querySelectorAll('[data-counter]')]
      const finish = () => {
        values.forEach(value => { value.textContent = value.dataset.counter })
        proof.classList.add('is-counted')
      }

      if (reduced.matches || !('IntersectionObserver' in window)) {
        finish()
        return
      }

      values.forEach(value => { value.textContent = '0' })
      const observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return
        observer.disconnect()
        const animate = () => {
          const startedAt = performance.now()
          const duration = 800
          let lastPaint = 0
          const tick = now => {
            const progress = Math.min((now - startedAt) / duration, 1)
            if (progress === 1 || now - lastPaint >= 50) {
              const eased = 1 - Math.pow(1 - progress, 3)
              lastPaint = now
              values.forEach(value => {
                const nextValue = String(Math.round(Number(value.dataset.counter) * eased))
                if (value.textContent !== nextValue) value.textContent = nextValue
              })
            }
            if (progress < 1) requestAnimationFrame(tick)
            else finish()
          }
          requestAnimationFrame(tick)
        }
        if ('requestIdleCallback' in window) requestIdleCallback(animate, { timeout: 900 })
        else setTimeout(animate, 120)
      }, { threshold: 0.35 })
      observer.observe(proof)
    })
  }

  const mobileCta = () => {
    const cta = document.querySelector('[data-mobile-cta]')
    const hero = document.querySelector('.hero')
    const contact = document.getElementById('contact')
    if (!cta || !hero || !contact || cta.dataset.ready) return
    cta.dataset.ready = 'true'

    if (!('IntersectionObserver' in window)) return
    let heroVisible = true
    let contactVisible = false
    const paint = () => cta.classList.toggle('is-visible', !heroVisible && !contactVisible)
    new IntersectionObserver(entries => {
      heroVisible = entries[0].isIntersecting
      paint()
    }, { threshold: 0.08 }).observe(hero)
    new IntersectionObserver(entries => {
      contactVisible = entries[0].isIntersecting
      paint()
    }, { threshold: 0.08 }).observe(contact)
  }

  const anchorScroll = () => {
    document.querySelectorAll('a[href^="#"]:not([data-anchor-ready]):not([data-scroll-top])').forEach(link => {
      link.dataset.anchorReady = 'true'
      link.addEventListener('click', event => {
        const hash = link.getAttribute('href')
        if (!hash || hash === '#') return
        const target = document.getElementById(hash.slice(1))
        if (!target) return
        event.preventDefault()
        const top = target.getBoundingClientRect().top + window.scrollY
        window.scrollTo({ top, behavior: reduced.matches ? 'auto' : 'smooth' })
        if (window.location.hash !== hash) history.pushState(null, '', hash)
      })
    })
  }

  const backToTop = () => {
    const button = document.querySelector('[data-scroll-top]')
    const footer = button?.closest('.site-footer')
    if (!button || !footer || button.dataset.dockReady) return
    button.dataset.dockReady = 'true'

    if (!('IntersectionObserver' in window)) return
    new IntersectionObserver(entries => {
      button.classList.toggle('is-docked', entries[0].isIntersecting)
    }, { threshold: 0.01 }).observe(footer)
  }

  const init = () => {
    reveal()
    galleries()
    accordions()
    counters()
    attribution()
    leadForms()
    mobileCta()
    anchorScroll()
    backToTop()

    const toggle = document.querySelector('[data-menu-toggle]')
    const nav = document.querySelector('[data-site-nav]')
    if (toggle && nav && !toggle.dataset.ready) {
      toggle.dataset.ready = 'true'
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true'
        toggle.setAttribute('aria-expanded', String(!open))
        nav.classList.toggle('is-open', !open)
      })
      nav.addEventListener('click', event => {
        if (!event.target.closest('a')) return
        toggle.setAttribute('aria-expanded', 'false')
        nav.classList.remove('is-open')
      })
    }

    document.querySelectorAll('[data-plan]').forEach(button => button.addEventListener('click', () => {
      const select = document.querySelector('[name="plan"]')
      if (select) {
        select.value = button.dataset.plan
        select.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }))

    document.querySelectorAll('[data-scroll-top]').forEach(link => link.addEventListener('click', event => {
      event.preventDefault()
      document.getElementById('top')?.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth' })
    }))
  }

  document.addEventListener('shopify:section:load', init)
  init()
})()
